import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Clock3,
  Filter,
  GraduationCap,
  Home,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  UserRound,
  Users,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import "./CoursesPage.css";
import {
  courseFallbackImage,
  formatCurrency,
  getCourseImage,
  getCoursePrice,
  getCourseSummary,
  getCourseTitle,
  listCourses,
} from "../api/courses";

function CourseCard({ course, isAr }) {
  const title = getCourseTitle(course, isAr);
  const summary = getCourseSummary(course, isAr);
  const price = getCoursePrice(course);
  const image = getCourseImage(course);
  const ratingValue = Number(course?.average_rating || course?.avg_rating || 0);
  const students = Number(course?.enrollments_count || course?.students_count || 0);

  const courseRef = String(course?.slug || course?.id || "").trim();
  const detailRef = courseRef && courseRef !== "html" ? courseRef : course?.id;
  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") {
      return;
    }

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = courseFallbackImage;
  };

  return (
    <div className="courses-page-card-wrap">
      <article className="courses-page-card">
        <Link to={`/courses/${detailRef}`} className="courses-page-card__media" aria-label={title}>
          <img src={image.src} alt={title} className="courses-page-card__image" onError={handleImageError} />
          <span className="courses-page-card__shade" />

          <div className="courses-page-card__badges">
            <span className="courses-page-pill courses-page-pill--light">
              <GraduationCap size={14} />
              {course?.level || "Beginner"}
            </span>
            <span className="courses-page-pill courses-page-pill--price">
              {course?.is_free ? (isAr ? "مجاني" : "Free") : formatCurrency(price)}
            </span>
          </div>
        </Link>

        <div className="courses-page-card__content">
          <div className="courses-page-card__topline">
            <span>
              <Sparkles size={14} />
              {course?.category || (isAr ? "كورس احترافي" : "Professional Course")}
            </span>
            <span>
              <Star size={14} />
              {ratingValue ? ratingValue.toFixed(1) : "0.0"}
            </span>
          </div>

          <h3 className="courses-page-card__title">
            <Link to={`/courses/${detailRef}`}>{title}</Link>
          </h3>
          <p className="courses-page-card__description">{summary}</p>

          <div className="courses-page-card__facts">
            <div>
              <Clock3 size={16} />
              <span>{course?.duration_text || (isAr ? "مرنة" : "Flexible")}</span>
            </div>
            <div>
              <BookOpen size={16} />
              <span>
                {course?.lessons_count || 0} {isAr ? "درس" : "lessons"}
              </span>
            </div>
            <div>
              <Users size={16} />
              <span>
                {students} {isAr ? "طالب" : "students"}
              </span>
            </div>
          </div>

          <div className="courses-page-card__footer">
            <div className="courses-page-card__instructor">
              <UserRound size={16} />
              <span>{course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group")}</span>
            </div>
            <Link to={`/courses/${detailRef}`} className="courses-page-card__button">
              <span>{isAr ? "عرض التفاصيل" : "View Details"}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function CoursesPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState(0);
  const [studentsCount, setStudentsCount] = useState("");

  const resetFilters = () => {
    setSearch("");
    setPriceRange([0, 10000]);
    setLevel("");
    setCategory("");
    setRating(0);
    setStudentsCount("");
  };

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const list = await listCourses();
        if (active) {
          setCourses(list);
        }
      } catch (err) {
        logger.error("Courses load error", err);
        if (active) {
          setError(isAr ? "فشل تحميل الكورسات." : "Failed to load courses.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  const filteredCourses = useMemo(() => {
    let result = courses;

    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((course) => {
        const haystack = [
          course?.title_ar,
          course?.title_en,
          course?.short_description_ar,
          course?.short_description_en,
          course?.category,
          course?.instructor_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    result = result.filter((course) => {
      const price = course?.is_free ? 0 : Number(course?.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (level) {
      result = result.filter((course) => course?.level === level);
    }

    if (category) {
      result = result.filter((course) => course?.category === category);
    }

    if (rating > 0) {
      result = result.filter((course) => Number(course?.average_rating || course?.avg_rating || 0) >= rating);
    }

    if (studentsCount) {
      result = result.filter((course) => Number(course?.enrollments_count || 0) >= Number(studentsCount));
    }

    return result;
  }, [courses, search, priceRange, level, category, rating, studentsCount]);

  const pageStats = useMemo(() => {
    const categories = new Set(courses.map((course) => course?.category).filter(Boolean));
    const freeCount = courses.filter((course) => course?.is_free).length;
    const lessonsCount = courses.reduce((total, course) => total + Number(course?.lessons_count || 0), 0);

    return [
      { key: "courses", value: courses.length, label: isAr ? "كورس متاح" : "Available Courses" },
      { key: "categories", value: categories.size, label: isAr ? "تخصص" : "Categories" },
      { key: "lessons", value: lessonsCount, label: isAr ? "درس" : "Lessons" },
      { key: "free", value: freeCount, label: isAr ? "كورس مجاني" : "Free Courses" },
    ];
  }, [courses, isAr]);

  const hasActiveFilters =
    search.trim() ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 10000 ||
    level ||
    category ||
    rating > 0 ||
    studentsCount;

  return (
    <div className="courses-page-v2" dir={isAr ? "rtl" : "ltr"}>
      <div className="courses-page-shell">
        <nav className="courses-page-breadcrumb" aria-label="breadcrumb">
          <Link to="/">
            <Home size={17} />
            {isAr ? "الرئيسية" : "Home"}
          </Link>
          <span>/</span>
          <strong>{isAr ? "الكورسات" : "Courses"}</strong>
        </nav>

        <section className="courses-page-hero">
          <div className="courses-page-hero__copy">
            <span className="courses-page-hero__eyebrow">
              <Sparkles size={16} />
              {isAr ? "مسارات تعليمية عملية" : "Practical Learning Paths"}
            </span>
            <h1>{isAr ? "تعلم مهارات جديدة بتجربة عصرية وواضحة" : "Learn new skills in a modern, focused experience"}</h1>
            <p>
              {isAr
                ? "استكشف كورسات مختارة بتفاصيل واضحة، مستويات منظمة، وبحث سريع يساعدك تصل للكورس المناسب بدون ازدحام."
                : "Explore curated courses with clear levels, helpful details, and fast filtering that gets you to the right course quickly."}
            </p>

            <div className="courses-page-search-panel">
              {/* <div className="courses-page-search-panel__head">
                <span>
                  <Search size={18} />
                  {isAr ? "ابحث في الكورسات" : "Search Courses"}
                </span>
                <small>
                  {filteredCourses.length} {isAr ? "نتيجة" : "results"}
                </small>
              </div> */}
              <label className="courses-page-search">
                <Search size={19} />
                <input
                  type="search"
                  placeholder={isAr ? "اسم الكورس، المدرب، أو التصنيف" : "Course name, instructor, or category"}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className="courses-page-search-panel__hint">
                <span>
                  {courses.length} {isAr ? "كورس إجمالي" : "total courses"}
                </span>
                {hasActiveFilters ? (
                  <button type="button" onClick={resetFilters}>
                    <RotateCcw size={14} />
                    {isAr ? "تصفير" : "Reset"}
                  </button>
                ) : null}
              </div>
            </div>

            {/* <div className="courses-page-hero__stats" aria-label={isAr ? "إحصائيات الكورسات" : "Course statistics"}>
              {pageStats.map((stat) => (
                <div key={stat.key}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div> */}
          </div>
        </section>

        <section className="courses-page-filters" aria-label={isAr ? "فلاتر الكورسات" : "Course filters"}>
          <div className="courses-page-filters__title">
            <span>
              <Filter size={18} />
              {isAr ? "فلترة النتائج" : "Filter Results"}
            </span>
            <button type="button" onClick={resetFilters}>
              <RotateCcw size={15} />
              {isAr ? "إعادة تعيين" : "Reset All"}
            </button>
          </div>

          <div className="courses-page-filters__grid">
            <div className="courses-page-filter">
              <label>{isAr ? "السعر" : "Price"}</label>
              <div className="courses-page-filter__split">
                <input
                  type="number"
                  placeholder={isAr ? "أدنى" : "Min"}
                  value={priceRange[0] === 0 ? "" : priceRange[0]}
                  onChange={(event) => setPriceRange([Number(event.target.value || 0), priceRange[1]])}
                />
                <input
                  type="number"
                  placeholder={isAr ? "أقصى" : "Max"}
                  value={priceRange[1] === 10000 ? "" : priceRange[1]}
                  onChange={(event) => setPriceRange([priceRange[0], Number(event.target.value || 10000)])}
                />
              </div>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "المستوى" : "Level"}</label>
              <select value={level} onChange={(event) => setLevel(event.target.value)}>
                <option value="">{isAr ? "الكل" : "All"}</option>
                <option value="Beginner">{isAr ? "مبتدئ" : "Beginner"}</option>
                <option value="Intermediate">{isAr ? "متوسط" : "Intermediate"}</option>
                <option value="Advanced">{isAr ? "متقدم" : "Advanced"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "الفئة" : "Category"}</label>
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">{isAr ? "الكل" : "All"}</option>
                <option value="Programming">{isAr ? "البرمجة" : "Programming"}</option>
                <option value="Design">{isAr ? "التصميم" : "Design"}</option>
                <option value="Business">{isAr ? "الأعمال" : "Business"}</option>
                <option value="Marketing">{isAr ? "التسويق" : "Marketing"}</option>
                <option value="Language">{isAr ? "اللغات" : "Language"}</option>
                <option value="Personal">{isAr ? "التنمية الشخصية" : "Personal Development"}</option>
                <option value="Tech">{isAr ? "التكنولوجيا" : "Tech"}</option>
                <option value="Data">{isAr ? "البيانات" : "Data Science"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التقييم" : "Rating"}</label>
              <select value={rating} onChange={(event) => setRating(Number(event.target.value))}>
                <option value="0">{isAr ? "الكل" : "All"}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
                <option value="5">5</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "الطلاب" : "Students"}</label>
              <select value={studentsCount} onChange={(event) => setStudentsCount(event.target.value)}>
                <option value="">{isAr ? "أي عدد" : "Any"}</option>
                <option value="10">10+</option>
                <option value="50">50+</option>
                <option value="100">100+</option>
              </select>
            </div>
          </div>
        </section>

        <div className="courses-page-section-head">
          <div>
            <span>{isAr ? "اختيارات مميزة" : "Featured Selection"}</span>
            <h2>{isAr ? "الكورسات المتاحة" : "Available Courses"}</h2>
          </div>
          <p>
            {/* {isAr
              ? "نتائج مرتبة في بطاقات حديثة تعرض أهم التفاصيل قبل الدخول للكورس."
              : "A clean card grid with the essentials visible before opening a course."} */}
          </p>
        </div>

        {loading ? (
          <div className="courses-page-state">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p>{isAr ? "جاري تحميل الكورسات..." : "Loading courses..."}</p>
          </div>
        ) : error ? (
          <div className="courses-page-alert">{error}</div>
        ) : filteredCourses.length === 0 ? (
          <div className="courses-page-empty">
            <Search size={34} />
            <h2>{isAr ? "لا توجد كورسات مطابقة" : "No matching courses"}</h2>
            <p>{isAr ? "جرّب تغيير كلمة البحث أو تصفير الفلاتر." : "Try a different search term or reset the filters."}</p>
            <button type="button" onClick={resetFilters}>
              {isAr ? "إعادة تعيين الفلاتر" : "Reset Filters"}
            </button>
          </div>
        ) : (
          <div className="courses-page-grid">
            {filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} isAr={isAr} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
